import { useEffect, useState, useRef } from "react";
import { Camera, Upload, Coins } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GlassCard, PageHeader } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Profile() {
    const { user, login } = useAuth(); // login function refetches user details
    const [form, setForm] = useState({
        phone: "",
        location: ""
    });
    const [loading, setLoading] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    const [pwdForm, setPwdForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
    const [pwdLoading, setPwdLoading] = useState(false);

    const fileInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (user) {
            setForm({
                phone: user.phone || "",
                location: user.location || ""
            });
        }
    }, [user]);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.patch("/employees/me/profile", form);
            toast.success("Profile details saved successfully!");
            // Refetch current user details
            const meRes = await api.get("/auth/me");
            login(meRes.data); // Updates the auth context user state
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (pwdForm.new_password !== pwdForm.confirm_password) {
            toast.error("New passwords do not match!");
            return;
        }
        setPwdLoading(true);
        try {
            await api.post("/auth/change-password", {
                old_password: pwdForm.old_password,
                new_password: pwdForm.new_password
            });
            toast.success("Password changed successfully!");
            setPwdForm({ old_password: "", new_password: "", confirm_password: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPwdLoading(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64 = reader.result;
                await uploadAvatar(base64);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadAvatar = async (base64) => {
        const tid = toast.loading("Updating profile picture...");
        try {
            const { data } = await api.patch("/employees/me/profile", { avatar: base64 });
            toast.success("Profile picture updated successfully!", { id: tid });
            const meRes = await api.get("/auth/me");
            login(meRes.data);
        } catch (err) {
            toast.error(formatApiError(err), { id: tid });
        }
    };

    const startCamera = async () => {
        setCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (e) {
            toast.error("Could not access camera");
            setCameraActive(false);
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/jpeg');
            uploadAvatar(dataUrl);
            stopCamera();
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            stream.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
        setCameraActive(false);
    };

    const downloadIdCard = async () => {
        toast.loading("Generating ID Card...", { id: "id-download" });
        try {
            const response = await api.get(`/documents/id-card/${user.id}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `id_card_${user.name.replace(/\s+/g, '_')}.pdf`;
            link.click();
            toast.success("Downloaded ID Card PDF", { id: "id-download" });
        } catch (e) {
            toast.error("Failed to download ID Card", { id: "id-download" });
        }
    };

    const resetDemoData = async () => {
        if (!window.confirm("Are you sure you want to reset all demo data? This will clear all changes and restore original screenshot employees.")) return;
        toast.loading("Resetting data...", { id: "reset-demo" });
        try {
            await api.post("/settings/reset-demo");
            toast.success("Demo data reset successfully!", { id: "reset-demo" });
            window.location.reload();
        } catch (e) {
            toast.error("Reset failed", { id: "reset-demo" });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const date = new Date(dateStr);
            const day = date.getDate();
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
        } catch (e) {
            return dateStr;
        }
    };

    const formatSalary = (salary) => {
        if (salary === undefined || salary === null) return "₹0";
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(salary);
    };

    const getInitials = (name) => {
        if (!name) return "EE";
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    useEffect(() => {
        const currentVideo = videoRef.current;
        return () => {
            if (currentVideo && currentVideo.srcObject) {
                const stream = currentVideo.srcObject;
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    return (
        <div>
            <PageHeader title="Profile" subtitle="Manage your contact details and view employment information." />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Summary and Password */}
                <div className="space-y-6">
                    {/* Left Card: Avatar & Summary */}
                    <GlassCard className="p-8 flex flex-col items-center border border-teal-100/50 dark:border-teal-900/50 justify-center text-center">
                        <div className="relative group w-28 h-28 mb-4">
                            {user?.avatar ? (
                                <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover border-2 border-teal-500 shadow-md" />
                            ) : (
                                <div className="w-full h-full rounded-full bg-teal-600 flex items-center justify-center text-white text-3xl font-bold shadow-md">
                                    {getInitials(user?.name)}
                                </div>
                            )}
                        </div>
                        <h2 className="font-heading font-bold text-xl text-slate-800 dark:text-slate-100">{user?.name}</h2>
                        <p className="text-xs font-semibold text-muted-foreground mt-0.5">{user?.designation || "Team Member"}</p>

                        <div className="flex gap-2 mt-3">
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/30">
                                {user?.role?.replace("_", " ")}
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400 border border-teal-200/50 dark:border-teal-800/30">
                                {user?.department || "General"}
                            </span>
                        </div>

                        <div className="mt-6 flex flex-col items-center gap-2">
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="bg-white border text-black hover:bg-gray-50 flex items-center gap-1 h-9 rounded-xl font-medium" onClick={() => fileInputRef.current?.click()}>
                                    <Upload className="w-3.5 h-3.5" /> Change photo
                                </Button>
                                <Button size="sm" variant="outline" className="bg-white border text-black hover:bg-gray-50 flex items-center gap-1 h-9 rounded-xl font-medium" onClick={cameraActive ? capturePhoto : startCamera}>
                                    <Camera className="w-3.5 h-3.5" /> {cameraActive ? "Capture" : "Webcam"}
                                </Button>
                                {cameraActive && (
                                    <Button size="sm" variant="destructive" onClick={stopCamera} className="h-9 rounded-xl">Cancel</Button>
                                )}
                                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">Available once every 15 days.</p>
                        </div>

                        {cameraActive && (
                            <div className="mt-4 relative rounded-lg overflow-hidden bg-black aspect-video w-full max-w-[280px] border shadow-md">
                                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                                <canvas ref={canvasRef} className="hidden" />
                            </div>
                        )}
                    </GlassCard>

                    {/* Change Password Card */}
                    <GlassCard className="p-8 border border-teal-100/50 dark:border-teal-900/50">
                        <h3 className="font-heading font-bold text-lg mb-4 text-slate-800 dark:text-slate-100 border-b pb-2">Change password</h3>
                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Current password</Label>
                                <Input 
                                    type="password" 
                                    required 
                                    value={pwdForm.old_password} 
                                    onChange={(e) => setPwdForm({ ...pwdForm, old_password: e.target.value })} 
                                    className="mt-1.5 text-xs" 
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">New password</Label>
                                <Input 
                                    type="password" 
                                    required 
                                    value={pwdForm.new_password} 
                                    onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} 
                                    className="mt-1.5 text-xs" 
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Confirm new password</Label>
                                <Input 
                                    type="password" 
                                    required 
                                    value={pwdForm.confirm_password} 
                                    onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })} 
                                    className="mt-1.5 text-xs" 
                                />
                            </div>
                            <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold h-11 mt-2" disabled={pwdLoading}>
                                {pwdLoading ? "Updating..." : "Update password"}
                            </Button>
                        </form>
                    </GlassCard>
                </div>

                {/* Right Card: Contact Details Form */}
                <GlassCard className="p-8 lg:col-span-2 border border-teal-100/50 dark:border-teal-900/50">
                    <h3 className="font-heading font-bold text-lg mb-6 text-slate-800 dark:text-slate-100 border-b pb-3">Contact details</h3>

                    <form onSubmit={handleSave} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Employee ID</Label>
                                <Input disabled value={user?.employee_id || ""} className="mt-1 bg-gray-50 dark:bg-slate-900 cursor-not-allowed font-mono text-xs" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Email</Label>
                                <Input disabled value={user?.email || ""} className="mt-1 bg-gray-50 dark:bg-slate-900 cursor-not-allowed text-xs" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Joined</Label>
                                <Input disabled value={formatDate(user?.joining_date)} className="mt-1 bg-gray-50 dark:bg-slate-900 cursor-not-allowed text-xs font-medium" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Phone</Label>
                                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 text-xs" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Location</Label>
                                <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 text-xs" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Monthly salary</Label>
                                <Input disabled value={formatSalary(user?.salary)} className="mt-1 bg-gray-50 dark:bg-slate-900 cursor-not-allowed text-xs font-semibold text-teal-600" />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Leave balance</Label>
                            <Input disabled value="18 paid / 10 sick / 6 casual" className="mt-1 bg-gray-50 dark:bg-slate-900 cursor-not-allowed text-xs font-medium" />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold h-11 px-6" disabled={loading}>
                                Save profile
                            </Button>
                            <Button type="button" onClick={downloadIdCard} variant="outline" className="bg-white border text-black hover:bg-gray-50 h-11 px-6 rounded-xl font-semibold">
                                Attendance QR PDF
                            </Button>
                            <Button type="button" onClick={resetDemoData} variant="outline" className="bg-white border text-red-600 hover:bg-red-50 border-red-200 h-11 px-6 rounded-xl font-semibold ml-auto">
                                Reset demo data
                            </Button>
                        </div>
                    </form>
                </GlassCard>
            </div>
        </div>
    );
}
